/**
 * DocumentPicker - which document the canvas is showing, chosen from its name.
 *
 * This WAS a docked pane on the right, and that was a poor trade: a permanent
 * column, for the width of the document you read constantly, spent on a choice
 * you make occasionally. The page carried two docked columns and squeezed the
 * text between them.
 *
 * So it hangs off the document's own name in the header - the one place a
 * reader already looks to know what they are reading, and therefore the
 * obvious place to press to read something else. Nothing was thinned out in
 * the move: the same overview row, the same `ReviewDocumentRow`s with their
 * risk, severity counts, decided progress and status.
 */

import React from 'react';
import clsx from 'clsx';
import { Menu } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import {
  PiCaretDownLight,
  PiFileTextLight,
  PiStackLight,
} from 'react-icons/pi';
import ReviewDocumentRow, { DocumentRowStat } from './surface/ReviewDocumentRow';
import { ReviewDocument } from '../../types/review';

export interface DocumentPickerTotals {
  total: number;
  decided: number;
  escalated: number;
  documentsComplete: number;
}

interface Props {
  documents: ReviewDocument[];
  docStats: Record<number, DocumentRowStat>;
  selectedDocumentId: number | 'all';
  /** The name shown on the trigger - the document currently on the canvas. */
  documentName: string | null;
  /** `DOCX · 12p`, when the viewer knows. */
  fileInfo?: string | null;
  totals: DocumentPickerTotals;
  /** True when nothing is left open anywhere in the session. */
  allDecided: boolean;
  finalized: boolean;
  onSelectDocument: (documentId: number) => void;
  onSelectAll: () => void;
}

const DocumentPicker: React.FC<Props> = ({
  documents,
  docStats,
  selectedDocumentId,
  documentName,
  fileInfo,
  totals,
  allDecided,
  finalized,
  onSelectDocument,
  onSelectAll,
}) => {
  const { t } = useTranslation();

  return (
    <Menu as="div" className="relative min-w-0">
      {({ open }) => (
        <>
          <Menu.Button
            data-testid="review-document-picker"
            data-open={open}
            title={documentName || t('Select a document')}
            /*
              IT HAS TO LOOK PRESSABLE WHEN IT IS NOT BEING POINTED AT.

              This used to be a transparent button with a transparent border,
              growing a wash only on hover - so at rest it was a filename with
              a small chevron beside it, indistinguishable from a label, and
              people did not know it opened anything. A control that only
              announces itself once you are already on it is not an
              affordance.

              So it carries a soft sunken fill and a hairline at REST, in the
              same tokens the toggle groups beside it use, and moves up a step
              on hover and again while open. Still quiet - it is a document's
              name, not a primary button - but unmistakably a control.

              It TRUNCATES: in the header's middle column a long filename must
              shorten, never push into the readout beside it.
            */
            className={clsx(
              'group flex items-center gap-2 min-w-0 max-w-[min(420px,40vw)]',
              'rounded-lg border px-2.5 py-1.5',
              'transition-colors duration-200 ease-slide',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40',
              open
                ? 'bg-surface-sunken border-champagne/40 shadow-sm'
                : 'bg-surface-sunken/60 border-hairline-soft hover:bg-surface-sunken hover:border-hairline',
            )}
          >
            <PiFileTextLight className="w-4 h-4 text-fg-muted flex-shrink-0" aria-hidden />
            <span className="text-[13px] font-medium text-fg-strong truncate min-w-0">
              {documentName || t('Select a document')}
            </span>
            {fileInfo && (
              <span className="text-[11px] text-fg-muted flex-shrink-0 hidden md:block tabular-nums">
                {fileInfo}
              </span>
            )}
            {/* Divided off from the name: the chevron belongs to the
                CONTROL, not to the filename it sits after. */}
            <span
              aria-hidden
              className="ms-0.5 h-4 w-px flex-shrink-0 bg-hairline-soft"
            />
            <PiCaretDownLight
              className={clsx(
                'w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ease-slide',
                open ? 'rotate-180 text-champagne' : 'text-fg-body group-hover:text-champagne',
              )}
              aria-hidden
            />
          </Menu.Button>

          {/*
            Opaque, like every other menu in the app: this opens over a
            document, and a translucent panel on white paper is a list you
            cannot read. Sized to the rows rather than the trigger - these
            carry three figures each and need the width.
          */}
          {/*
            ANCHORED, not absolutely positioned.

            The first cut placed this with `absolute` inside the toolbar row,
            and it was invisible: the frame around the row is
            `overflow-hidden`, so the menu was clipped out of existence while
            being perfectly present in the DOM. Headless UI's `anchor` renders
            it in a portal and positions it against the trigger, which is what
            every other menu on this page already does.
          */}
          <Menu.Items
            data-testid="review-document-picker-menu"
            anchor="bottom"
            transition
            /*
              CAPPED TO WHAT IS LEFT BELOW THE TRIGGER, not to a share of the
              viewport. `max-h-[70vh]` measures the wrong thing: the panel
              hangs from a bar near the top of the window, so on a session
              with a dozen documents 70vh ran off the bottom edge and the
              last rows were unreachable.

              Headless UI publishes `--anchor-max-height` - the room actually
              available on the side it chose to open - so the cap is that,
              less a margin so the panel ends clear of the window edge rather
              than flush against it. The list scrolls inside whatever is
              left, at any anchor position.
            */
            className="[--anchor-gap:6px] [--anchor-padding:16px] z-[300] w-[380px] max-w-[92vw]
                       max-h-[min(70vh,calc(var(--anchor-max-height,70vh)-16px))]
                       overflow-y-auto custom-scrollbar-main
                       rounded-xl bg-surface ring-1 ring-black/[0.05] dark:ring-white/[0.08]
                       shadow-xl focus:outline-none p-1.5
                       origin-top transition duration-150 ease-pop
                       data-[closed]:opacity-0 data-[closed]:scale-95"
          >
            {/*
              The way back to every document at once. A row in the same list,
              but deliberately WITHOUT the three-column anatomy: it is a
              summary, and a status glyph and a decided/total would have it
              impersonate a document.
            */}
            <Menu.Item>
              {() => (
                <button
                  type="button"
                  data-testid="review-document-overview"
                  data-active={selectedDocumentId === 'all'}
                  onClick={onSelectAll}
                  className={clsx(
                    'group relative w-full text-start px-3 py-2.5 rounded-xl mb-0.5',
                    'transition-all duration-200 ease-slide',
                    selectedDocumentId === 'all'
                      ? 'bg-champagne/10 ring-1 ring-inset ring-champagne/50'
                      : 'hover:bg-surface-sunken/70',
                  )}
                >
                  <span
                    aria-hidden
                    className={clsx(
                      'absolute start-0 top-0 bottom-0 w-[3px] rounded-s-xl bg-champagne',
                      'transition-opacity duration-200 ease-slide',
                      selectedDocumentId === 'all' ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <div className="grid grid-cols-[26px_1fr_auto] items-center gap-3">
                    <span className="w-[26px] h-[26px] rounded-full bg-surface-sunken
                                     flex items-center justify-center flex-shrink-0">
                      <PiStackLight className="w-3.5 h-3.5 text-fg-muted" aria-hidden />
                    </span>

                    <div className="min-w-0">
                      <p className={clsx(
                        'text-[13px] truncate leading-snug',
                        selectedDocumentId === 'all'
                          ? 'font-medium text-fg-strong'
                          : 'font-medium text-fg-body',
                      )}>
                        {t('All documents')}
                      </p>
                      {/* Reads as a sentence rather than a pair of ratios. */}
                      <p className="text-[11px] text-fg-muted truncate mt-0.5">
                        <span className="tabular-nums">
                          {totals.decided} {t('of')} {totals.total} {t('findings decided')}
                        </span>
                        {totals.escalated > 0 && (
                          <span className="tabular-nums text-purple-600 dark:text-purple-400">
                            {' · '}{totals.escalated} {t('escalated')}
                          </span>
                        )}
                        {totals.documentsComplete > 0 && (
                          <>
                            <span className="mx-1 text-fg-muted/60" aria-hidden>·</span>
                            <span className="tabular-nums">
                              {totals.documentsComplete}{' '}
                              {t(totals.documentsComplete === 1
                                ? 'document complete'
                                : 'documents complete')}
                            </span>
                          </>
                        )}
                      </p>
                    </div>

                    {(finalized || allDecided) && (
                      <span className="flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full
                                       bg-success-50 dark:bg-success-900/30 text-success-600 dark:text-success-400">
                        {finalized ? t('Finalized') : t('All decided')}
                      </span>
                    )}
                  </div>
                </button>
              )}
            </Menu.Item>

            {/* The overview is not one of the documents - a hairline says so. */}
            <div className="my-1.5 border-t border-hairline-soft" />

            {documents.map((doc, i) => (
              <Menu.Item key={doc.id}>
                {() => (
                  <ReviewDocumentRow
                    index={i}
                    fileName={doc.file_name}
                    stat={docStats[doc.id]}
                    isProcessing={doc.status === 'processing'}
                    isSelected={selectedDocumentId === doc.id}
                    /*
                      In a dropdown, selecting IS opening: the menu closes and
                      the canvas swaps. The row's separate open icon exists in
                      a list you stay in, and here there is no staying.
                    */
                    onSelect={() => onSelectDocument(doc.id)}
                    onOpen={() => onSelectDocument(doc.id)}
                    labels={{
                      notStarted: t('Not started'),
                      inReview: t('In review'),
                      complete: t('Complete'),
                      findings: t('findings'),
                      critical: t('critical'),
                      major: t('major'),
                      risk: t('Risk'),
                      topRisk: t('Highest risk score in this document'),
                      open: t('Open document'),
                    }}
                  />
                )}
              </Menu.Item>
            ))}
          </Menu.Items>
        </>
      )}
    </Menu>
  );
};

export default DocumentPicker;
